import React, {useRef} from 'react';

import {Dropdown, IconButton, Text, WithToggle, MenuListItem} from '@albert-io/atomic';

import type {SortOption} from 'client/Reports/types';

import './header-cell.scss';

interface BaseHeaderCellProps {
  title: JSX.Element;
}

interface SortableHeaderCellProps extends BaseHeaderCellProps {
  showSort: true;
  isCurrentSort: boolean;
  onSortClick: (sortKey: string, sortDirection: SortOption) => void;
  sortDirection: SortOption;
  sortBy: string | null;
  sorts: readonly {label: string; sortKey: string}[];
}

interface NonSortableHeaderCellProps extends BaseHeaderCellProps {
  showSort: false;
}

type HeaderCellProps = SortableHeaderCellProps | NonSortableHeaderCellProps;

export const HeaderCell = ({title, showSort = true, ...props}: HeaderCellProps) => {
  const toggleRef = useRef(null);
  return (
    <th className='reports-table__th'>
      <div className='flex items-center justify-between'>
        {title}
        {showSort && (
          <WithToggle>
            {({on, onClick}) => {
              const {isCurrentSort, onSortClick, sortDirection, sortBy, sorts} =
                props as SortableHeaderCellProps;

              return (
                <>
                  <IconButton
                    color={isCurrentSort ? 'primary' : 'secondary'}
                    className='o-table__header-sort'
                    label='sort'
                    iconStyle='regular'
                    size='s'
                    icon={isCurrentSort && sortDirection === 'desc' ? 'arrow-down' : 'arrow-up'}
                    onClick={onClick}
                    ref={toggleRef}
                  />
                  {on ? (
                    <Dropdown.Tray
                      className='flex flex-col overflow-hidden rounded-x2 border border-primary background-primary shadow'
                      positionFixed
                      placement='bottom-end'
                      target={toggleRef.current}
                      style={{zIndex: 11}}
                      modifiers={{
                        flip: {
                          enabled: false
                        }
                      }}
                    >
                      <Text color='tertiary' size='s' className='reports-table__header-sort-label'>
                        Sort by...
                      </Text>
                      <div className='p-x1'>
                        {sorts.map(({label, sortKey}) => {
                          const isSelected = sortBy === sortKey;
                          return (
                            <MenuListItem
                              key={sortKey}
                              selected={isSelected}
                              onMenuItemClick={() => onSortClick(sortKey, 'desc')}
                              className='reports-table__header-sort-item'
                            >
                              <Text
                                color={isSelected ? 'brand' : 'primary'}
                                className='reports-table__header-sort-item-label'
                              >
                                {label}
                              </Text>
                              {isSelected && (
                                <Text color='info' size='xs'>
                                  {sortDirection === 'desc' ? 'Descending' : 'Ascending'}
                                </Text>
                              )}
                            </MenuListItem>
                          );
                        })}
                      </div>
                    </Dropdown.Tray>
                  ) : null}
                </>
              );
            }}
          </WithToggle>
        )}
      </div>
    </th>
  );
};
